import { z } from 'zod';
import type { Konditionen } from './abrechnung';

/*
 * Abrechnung zwischen DXL und den Marktplätzen über das Stripe-Konto von DXL („Modell A“):
 * Paket + Add-ons als monatliches Abo (Stripe Checkout / Billing), Guthaben für die KI von DXL per Vorkasse.
 * Stripe Connect der Marktplätze (Auszahlungen an Händler) bleibt davon unberührt.
 *
 * Beträge der Oberfläche in Cent; das Guthaben rechnet intern in Mikro-Cent (1 Cent = 1.000.000), wie der KI-Verbrauch.
 */

const Cent = z.number({ invalid_type_error: 'Bitte Betrag angeben' }).int('Betrag in Cent als Ganzzahl').min(0, 'Nicht negativ').max(10_000_000_00);

/* ------------------------------------------------------------------ Add-ons */

/**
 * Was ein Add-on freischaltet. Module (ankauf) und die Lizenz (whitelabel) werden beim Buchen am Marktplatz gesetzt
 * und beim Ende wieder entfernt, wenn das Add-on sie gesetzt hat. Die übrigen Funktionen (SPERRBARE_ADDONS) prüft
 * die Plattform bei jedem Zugriff (lib/addons.ts im Backend).
 */
export const ADDON_FUNKTIONEN = {
  ankauf: { titel: 'Uhren-Ankauf', text: 'Modul Ankauf mit Uhren-Katalog', modul: 'ankauf' },
  ki_dxl: { titel: 'KI von DXL', text: 'KI ohne eigenen Schlüssel (Verbrauch über Guthaben)', modul: null },
  channels: { titel: 'Channels', text: 'Feeds und Kanäle (Google, Idealo, Amazon …)', modul: null },
  buybox: { titel: 'Buy Box', text: 'Buy Box mit Gruppierung nach Merkmalen', modul: null },
  shop: { titel: 'Marktplatz-Shop', text: 'Übertragung in den Marktplatz-Shop', modul: null },
  whitelabel: { titel: 'Whitelabel', text: 'Eigenes Branding ohne DXL-Hinweis', modul: null },
  sonstiges: { titel: 'Sonstiges', text: 'Leistung ohne technische Freischaltung', modul: null },
} as const;
export type AddonFunktion = keyof typeof ADDON_FUNKTIONEN;
export const ADDON_FUNKTION_IDS = Object.keys(ADDON_FUNKTIONEN) as [AddonFunktion, ...AddonFunktion[]];

/**
 * Funktionen, die ein Add-on freischaltet und die ohne Add-on gesperrt sind – aber nur bei Marktplätzen, die selbst
 * ein Paket gebucht haben. Marktplätze ohne gebuchtes Paket (von DXL angelegt, Vertrag ohne Buchung) behalten alles
 * (Bestandsschutz). Frei ist eine Funktion, wenn sie im Paket enthalten, dazugebucht oder von DXL freigeschaltet ist.
 */
export const SPERRBARE_ADDONS = ['ki_dxl', 'channels', 'buybox', 'shop'] as const satisfies readonly AddonFunktion[];
export type SperrbaresAddon = (typeof SPERRBARE_ADDONS)[number];
export const istSperrbaresAddon = (f: string): f is SperrbaresAddon => (SPERRBARE_ADDONS as readonly string[]).includes(f);
export const SperrbareAddonsSchema = z.array(z.enum(SPERRBARE_ADDONS)).max(SPERRBARE_ADDONS.length).default([]);

/** Hinweis, wenn eine Funktion fehlt (Fehlercode addon_fehlt) */
export const addonFehltText = (f: AddonFunktion) =>
  `${ADDON_FUNKTIONEN[f].titel} ist in Ihrem Paket nicht enthalten. Sie können es unter Paket & Abrechnung dazubuchen.`;

export const AddonSchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen angeben').max(80),
  beschreibung: z.string().trim().max(500).default(''),
  preisCent: Cent,
  funktion: z.enum(ADDON_FUNKTION_IDS, { errorMap: () => ({ message: 'Bitte wählen, was das Add-on freischaltet' }) }),
  position: z.number().int().min(0).max(1000).default(0),
  aktiv: z.boolean().default(true),
});
export type AddonEingabe = z.input<typeof AddonSchema>;

/** Abgleich mit Stripe (Product + monatlicher Price) */
export interface StripeSyncStand {
  produkt: string | null;
  preis: string | null;
  /** Betrag des aktuellen Stripe-Price in Cent */
  preisCent: number | null;
  syncAm: string | null;
  fehler: string | null;
  /** Price passt zum aktuellen Preis */
  aktuell: boolean;
}

export interface Addon {
  id: string;
  name: string;
  beschreibung: string;
  preisCent: number;
  funktion: AddonFunktion;
  position: number;
  aktiv: boolean;
  stripe: StripeSyncStand;
  /** Namen der Marktplätze, die das Add-on gebucht haben */
  nutzung: string[];
}

/** Ergebnis beim Speichern: ohne Stripe-Zugang ein Hinweis statt Abgleich */
export interface StripeSyncHinweis {
  stripe: 'synchronisiert' | 'fehler' | 'nicht_eingerichtet';
  meldung: string | null;
}

/* ------------------------------------------------------------------ Abo des Marktplatzes */

export const ABO_STATUS = {
  unvollstaendig: 'Zahlung ausstehend',
  testphase: 'Testphase',
  aktiv: 'Aktiv',
  ueberfaellig: 'Zahlung überfällig',
  gekuendigt: 'Gekündigt zum Periodenende',
  beendet: 'Beendet',
} as const;
export type AboStatus = keyof typeof ABO_STATUS;
export const ABO_STATUS_TON: Record<AboStatus, 'gut' | 'warnung' | 'schlecht' | 'neutral' | 'akzent'> = {
  unvollstaendig: 'warnung', testphase: 'akzent', aktiv: 'gut', ueberfaellig: 'schlecht', gekuendigt: 'warnung', beendet: 'neutral',
};

/** Stripe-Status → Status der Plattform */
export function aboStatusAus(stripeStatus: string, kuendigungZumEnde: boolean): AboStatus {
  if (stripeStatus === 'canceled' || stripeStatus === 'incomplete_expired') return 'beendet';
  if (stripeStatus === 'past_due' || stripeStatus === 'unpaid') return 'ueberfaellig';
  if (stripeStatus === 'incomplete' || stripeStatus === 'paused') return 'unvollstaendig';
  if (kuendigungZumEnde) return 'gekuendigt';
  if (stripeStatus === 'trialing') return 'testphase';
  return 'aktiv';
}

/** Abo läuft (Paket und Add-ons werden über Stripe bezahlt, nicht im Monatslauf) */
export const aboLaeuft = (s: AboStatus | null | undefined) => !!s && s !== 'beendet';

export interface AddonAngebot {
  id: string;
  name: string;
  beschreibung: string;
  preisCent: number;
  funktion: AddonFunktion;
}

export interface MeinAddon {
  id: string;
  name: string;
  preisCent: number;
  funktion: AddonFunktion;
  /** gekündigt zum (inklusive), null = läuft */
  bis: string | null;
  quelle: 'stripe' | 'rechnung';
}

export interface AboStand {
  status: AboStatus;
  /** Ende der laufenden Abrechnungsperiode (ISO) */
  periodeEnde: string | null;
  /** gekündigt zum (ISO), null = nicht gekündigt */
  kuendigungZum: string | null;
  letzteZahlung: string | null;
  /** Zahlungsart & Rechnungen über das Stripe-Kundenportal */
  portal: boolean;
}

export const AboBuchenSchema = z.object({
  paketId: z.string().uuid(),
  addonIds: z.array(z.string().uuid()).max(20).default([]),
  zustimmung: z.literal(true, { errorMap: () => ({ message: 'Bitte bestätigen Sie die Konditionen.' }) }),
});
export type AboBuchenEingabe = z.input<typeof AboBuchenSchema>;

/** Antwort: Checkout (Weiterleitung zu Stripe), geändertes Abo oder Buchung ohne Zahlung (ohne Stripe) */
export type AboBuchenErgebnis =
  | { art: 'checkout'; url: string }
  | { art: 'geaendert'; sofort: string[]; zumPeriodenende: string[]; ab: string | null }
  | { art: 'ohne_zahlung'; paket: string; ab: string };

/** Wechselregel mit Stripe: teurer/gleich → sofort mit anteiliger Berechnung, günstiger → zum Periodenende */
export function aboWechselArt(alt: Konditionen | null, neu: Konditionen): 'sofort' | 'periodenende' {
  if (!alt) return 'sofort';
  return neu.grundgebuehr >= alt.grundgebuehr ? 'sofort' : 'periodenende';
}

/* ------------------------------------------------------------------ Guthaben (KI von DXL) */

/** 1 Cent in der internen Einheit des Guthabens */
export const MIKROCENT = 1_000_000;
/** Mindestbetrag einer Aufladung (netto) */
export const KI_GUTHABEN_MINDEST_CENT = 1000;
export const KI_GUTHABEN_HOECHST_CENT = 1_000_000;
/** Unter diesem Stand weist die Oberfläche auf niedriges Guthaben hin */
export const KI_GUTHABEN_NIEDRIG_CENT = 500;

export const KiGuthabenPaketSchema = z.object({
  betragCent: z.number().int().min(KI_GUTHABEN_MINDEST_CENT, 'Mindestens 10 €').max(KI_GUTHABEN_HOECHST_CENT, 'Höchstens 10.000 €'),
  bonusCent: z.number().int().min(0, 'Nicht negativ').max(KI_GUTHABEN_HOECHST_CENT).default(0),
  aktiv: z.boolean().default(true),
});
export const KiGuthabenPaketeSchema = z.object({ pakete: z.array(KiGuthabenPaketSchema).max(12) });
export type KiGuthabenPaketEingabe = z.input<typeof KiGuthabenPaketSchema>;

export interface KiGuthabenPaket {
  id: string;
  betragCent: number;
  bonusCent: number;
  aktiv: boolean;
  position: number;
}

export const KiGuthabenAufladenSchema = z.union([
  z.object({ paketId: z.string().uuid() }),
  z.object({
    betragCent: z
      .number({ invalid_type_error: 'Bitte Betrag angeben' })
      .int('Betrag in Cent als Ganzzahl')
      .min(KI_GUTHABEN_MINDEST_CENT, 'Der Mindestbetrag ist 10 €.')
      .max(KI_GUTHABEN_HOECHST_CENT, 'Höchstens 10.000 € je Aufladung.'),
  }),
]);
export type KiGuthabenAufladenEingabe = z.input<typeof KiGuthabenAufladenSchema>;

export const KI_GUTHABEN_ART = {
  aufladung: 'Aufladung',
  reservierung: 'Reservierung',
  verbrauch: 'Verbrauch',
  freigabe: 'Freigabe',
  korrektur: 'Korrektur durch DXL',
} as const;
export type KiGuthabenArt = keyof typeof KI_GUTHABEN_ART;

export interface KiGuthabenStand {
  /** gebuchtes Guthaben abzüglich offener Reservierungen */
  verfuegbarCent: number;
  reserviertCent: number;
  /** Guthaben wird für die KI von DXL benötigt (Stripe von DXL eingerichtet, Quelle „KI von DXL“) */
  pflicht: boolean;
  /** KI von DXL ist gewählt */
  dxl: boolean;
  niedrig: boolean;
  /** Aufladen über Stripe möglich */
  aufladbar: boolean;
  darfAufladen: boolean;
  pakete: KiGuthabenPaket[];
  mindestCent: number;
}

export interface KiGuthabenBuchung {
  id: string;
  art: KiGuthabenArt;
  /** Wirkung auf das verfügbare Guthaben in Cent (zwei Nachkommastellen: Bruchteile von Cent) */
  betragCent: number;
  standCent: number;
  text: string;
  zeit: string;
}

export interface KiGuthabenBuchungSeite {
  eintraege: KiGuthabenBuchung[];
  weiter: string | null;
}

export const KiGuthabenKorrekturSchema = z.object({
  /** Gutschrift positiv, Belastung negativ */
  betragCent: z.number({ invalid_type_error: 'Bitte Betrag angeben' }).int().min(-KI_GUTHABEN_HOECHST_CENT).max(KI_GUTHABEN_HOECHST_CENT).refine((x) => x !== 0, 'Bitte einen Betrag ungleich 0 angeben'),
  text: z.string().trim().min(3, 'Bitte einen Grund angeben').max(200),
});
export type KiGuthabenKorrekturEingabe = z.input<typeof KiGuthabenKorrekturSchema>;

/** Zeile der Guthaben-Übersicht für DXL */
export interface KiGuthabenZeile {
  mandantId: string;
  kuerzel: string;
  name: string;
  verfuegbarCent: number;
  reserviertCent: number;
  dxl: boolean;
  letzteAufladung: string | null;
}

export interface KiGuthabenSeite {
  eintraege: KiGuthabenZeile[];
  weiter: string | null;
}

/** Mikro-Cent → Cent mit zwei Nachkommastellen (für Anzeigen mit Bruchteilen eines Cents) */
export const mikrocentZuCentGenau = (m: number) => Math.round(m / 10_000) / 100;
